<template>
  <v-container fluid class="navigation-tools-page">
    <div class="text-h4 font-weight-light mb-6 black--text">Navigation Tools</div>

    <v-row>
      <!-- ROUTING DEBUGGER -->
      <v-col cols="12" md="7">
        <v-card outlined class="minimal-card mb-6">
          <v-card-title class="grey lighten-4 black--text font-weight-medium">
            <v-icon left color="black">mdi-routes</v-icon> Routing Diagnostics
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field v-model="route.startId" label="Start ID (e.g. 32-206)" outlined dense color="black"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="route.endId" label="End ID (e.g. 32-249)" outlined dense color="black"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select v-model="route.option" :items="['fastest', 'avoid_stairs', 'avoid_elevators']" label="Routing Option" outlined dense color="black" item-color="black"></v-select>
              </v-col>
            </v-row>
            <v-btn outlined color="black" @click="runDebugRoute" :loading="loadingRoute" block>
              Run Algorithm Analysis
            </v-btn>

            <div v-if="routeResult" class="mt-6">
              <v-alert v-if="!routeResult.success" outlined type="error" class="mb-4">
                <strong>Failure:</strong> {{ routeResult.error }}
              </v-alert>
              <v-alert v-else outlined type="success" class="mb-4">
                Path Found! ({{ routeResult.route && routeResult.route.features ? routeResult.route.features.length : 0 }} UI features, {{ routeResult.rawDijkstraSteps ? routeResult.rawDijkstraSteps.length : 0 }} Dijkstra edges)
              </v-alert>

              <v-expansion-panels flat accordion class="minimal-panels">
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-medium">Algorithm Step-by-Step Breakdown</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Seq</th>
                            <th class="text-left">Node ID</th>
                            <th class="text-left">Edge ID</th>
                            <th class="text-left">Cost (Length)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="step in routeResult.rawDijkstraSteps" :key="step.seq">
                            <td>{{ step.seq }}</td>
                            <td>{{ step.node_id }} <span v-if="step.node_id > 900000000" class="grey--text caption">(Virtual)</span></td>
                            <td>{{ step.edge_id }} <span v-if="step.edge_id < 0 && step.edge_id > -3000000" class="grey--text caption">(Virtual Snap)</span><span v-if="step.edge_id < -3000000" class="grey--text caption">(Virtual Door Link)</span></td>
                            <td>{{ step.cost }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-medium">Start Location Topology</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!routeResult.startLocationData" class="red--text mb-2">Error: Start ID not found in database!</div>
                    <pre class="json-dump">{{ { Object: routeResult.startLocationData, NearestPaths: routeResult.nearestStartPaths } }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-medium">Destination Topology</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="!routeResult.endLocationData" class="red--text mb-2">Error: End ID not found in database!</div>
                    <pre class="json-dump">{{ { Object: routeResult.endLocationData, NearestPaths: routeResult.nearestEndPaths } }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel v-if="routeResult.success">
                  <v-expansion-panel-header class="font-weight-medium">Final UI Feature Collection</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="json-dump">{{ routeResult.route }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- EXPORT / IMPORT -->
      <v-col cols="12" md="5">
        <v-card outlined class="minimal-card">
          <v-card-title class="grey lighten-4 black--text font-weight-medium">
            <v-icon left color="black">mdi-database-sync-outline</v-icon> Data Transfer
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-6">
            <div class="text-body-2 mb-4 grey--text text--darken-2">
              Export all navigation objects, paths, and room associations (doors) for a specific building to a JSON file, or import an existing file.
            </div>

            <v-select 
                v-model="transferDataType" 
                :items="[{text: 'Navigation Data', value: 'nav'}, {text: 'Group Data', value: 'groups'}]" 
                label="Data to Transfer" 
                outlined 
                dense 
                color="black">
            </v-select>
            <v-text-field 
                v-if="transferDataType === 'nav'"
                v-model="transferBuildingId" 
                label="Target Building ID" 
                outlined dense color="black" class="mb-2">
            </v-text-field>
            
            <v-btn dark color="grey darken-3" @click="exportData" :loading="loadingTransfer" block class="mb-4" elevation="0">
              <v-icon left>mdi-download</v-icon> Export to JSON
            </v-btn>

            <v-divider class="my-6"></v-divider>

            <div class="text-subtitle-2 mb-2 font-weight-bold">Import Data</div>
            <v-alert outlined type="warning" dense class="caption">
              Importing will completely overwrite and replace all existing navigation points, paths, and door associations for this building.
            </v-alert>

            <v-file-input
              v-model="importFile"
              accept=".json"
              label="Select JSON File"
              outlined
              dense
              color="black"
              prepend-icon="mdi-code-json"
            ></v-file-input>

            <v-btn outlined color="red darken-2" @click="confirmImport" :disabled="!importFile || loadingTransfer" :loading="loadingTransfer" block>
              <v-icon left>mdi-upload</v-icon> Wipe & Import
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Import Warning Dialog -->
    <v-dialog v-model="importDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 red--text">Confirm Destructive Import</v-card-title>
        <v-card-text class="pt-3 text-body-1">
          Are you absolutely sure you want to import this file into Building <strong>{{ transferBuildingId }}</strong>? <br><br>
          This action will permanently delete all current navigation data for this building before importing the new data.
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="importDialog = false">Cancel</v-btn>
          <v-btn color="red darken-2" dark elevation="0" @click="executeImport">Yes, Overwrite Data</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: 'MitAdminNavigationDebug',
  data() {
    return {
      route: { startId: '', endId: '', option: 'fastest' },
      transferBuildingId: '32',
      transferDataType: 'nav', // default to nav export
      loadingRoute: false,
      loadingTransfer: false,
      routeResult: null,
      importFile: null,
      importDialog: false
    };
  },
  methods: {
    async runDebugRoute() {
      if(!this.route.startId || !this.route.endId) return alert("Start and End required");
      this.loadingRoute = true;
      try {
        const res = await axios.get('/api/v2/admin/spaces/navigation/debug', {
          params: { startMitId: this.route.startId, endMitId: this.route.endId, routingOption: this.route.option }
        });
        this.routeResult = res.data;
      } catch (err) {
        alert("Diagnostics Failed: " + err.message);
      } finally {
        this.loadingRoute = false;
      }
    },
    
    async exportData() {
        if (this.transferDataType === 'nav' && !this.transferBuildingId) return alert("Building ID required");
        this.loadingTransfer = true;
        try {
            const endpoint = this.transferDataType === 'nav' 
            ? `/api/v2/admin/spaces/buildings/${this.transferBuildingId}/navigation/export`
            : `/api/v2/admin/spaces/groups/export`;

            const res = await axios.get(endpoint);
            
            const fileName = this.transferDataType === 'nav' 
            ? `csail_nav_export_${this.transferBuildingId}.json`
            : `csail_neighborhoods_export.json`;

            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            alert("Export Failed: " + err.message);
        } finally {
            this.loadingTransfer = false;
        }
    },

    confirmImport() {
      if (!this.transferBuildingId || !this.importFile) return;
      this.importDialog = true;
    },

    executeImport() {
      this.importDialog = false;
      this.loadingTransfer = true;
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const payload = JSON.parse(e.target.result);
          
          // Route to the correct endpoint based on the dropdown selection
          const endpoint = this.transferDataType === 'nav' 
            ? `/api/v2/admin/spaces/buildings/${this.transferBuildingId}/navigation/import`
            : `/api/v2/admin/spaces/groups/import`;

          await axios.post(endpoint, payload);
          
          alert(`${this.transferDataType === 'nav' ? 'Navigation' : 'Neighborhood'} import successful! The data has been replaced.`);
          this.importFile = null;
        } catch (err) {
          alert("Import Failed: Make sure the file is valid JSON. Server error: " + err.message);
        } finally {
          this.loadingTransfer = false;
        }
      };
      
      reader.readAsText(this.importFile);
    }
  }
};
</script>

<style scoped>
.navigation-tools-page {
  background-color: #fafafa;
  min-height: 100vh;
}
.minimal-card {
  border-radius: 8px;
  border-color: #e0e0e0;
}
.minimal-panels {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.json-dump {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  max-height: 350px;
  overflow-y: auto;
  font-size: 13px;
  color: #333;
  border: 1px solid #e0e0e0;
}
</style>